import * as H from "history";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../redux/rootReducer";
import React, {useEffect, useState} from "react";
import {Step, wizardMap} from "../WizardMap";
import {addVisitedStep} from "../state/navigationSlice";
import {includes} from "lodash";
import usePageTracking from "../../googleHook";

export type NavigationProps = {
    gotoNextSlide: (step: Step) => void;
    gotoPrevSlide: () => void;
    jumpTo: (step: Step) => void;
};

const PARAM_JUMP_BACK = "jumpBack";

type PathState = {
    oldPath: string[];
    curSlide: Slide | null,
    leavingSlide: Slide | null
};

type Slide = {
    step: Step,
    direction: Direction;
};

export enum Direction {
    LEFT,
    RIGHT
}

export const invertDirection = (val: Direction) => val === Direction.RIGHT ? Direction.LEFT : Direction.RIGHT;

export function useNavigation(history: H.History, initialStep: Step) {

    const dispatch = useDispatch();

    usePageTracking(history);

    const gl = history.location.search.replace(`?${PARAM_JUMP_BACK}`, "");

    const {visitedSteps, journeySuccessful} = useSelector((state: RootState) => state.navigation);

    const [navState, setNavState] = useState<PathState>({
        oldPath: [],
        curSlide: {step: initialStep, direction: Direction.RIGHT},
        leavingSlide: null
    });

    const nextSlide = (step: Step) => {
        dispatch(addVisitedStep(step));
        history.push(history.location.pathname + "/" + step + gl);
    };

    const prevSlide = () => history.goBack();

    const jumpTo = (step: Step) => history.push({
        pathname: history.location.pathname + "/" + step,
        search:  PARAM_JUMP_BACK + history.location.search
    });

    const renderStep = (Step: Step) => [Step]
        .map(step => wizardMap[step])
        .map((EnhancedSlide, index) => (
            <EnhancedSlide
                key={`slide ${index}`}
                gotoNextSlide={nextSlide}
                gotoPrevSlide={prevSlide}
                jumpTo={jumpTo}
            />));

    useEffect(() => {
        const historyArray: string[] = history.location.pathname.split("/");
        const curStep = historyArray[historyArray.length - 1] as Step;

        console.log("curStep", curStep);
        if (journeySuccessful) {
            // navigates to confirmation if journey was successful and keeps confirmation sticky
            if (curStep !== Step.CONFIRMATION) {
                history.push(`/${Step.CONFIRMATION}${gl}`);
            }
        } else if (curStep !== initialStep && !includes(visitedSteps, curStep)) {
            // prevents user from accessing routes he did not visit yet
            history.push(`/${initialStep}${gl}`);
        }
    });

    // navigates stack depending on browser actions
    useEffect(() => history.listen((location: any, action: H.Action) => {

        const historyArray: string[] = location.pathname.split("/");

        console.log("histArr", historyArray);

        const curStep = historyArray[historyArray.length - 1] as Step;
        let prevStep: Step;
        let direction: Direction;

        if (action === "POP") {
            if (historyArray.length > navState.oldPath.length) {
                // forward action
                direction = Direction.RIGHT;
                prevStep = historyArray[historyArray.length - 2] as Step;
            } else {
                // back action
                direction = Direction.LEFT;
                prevStep = navState.oldPath[navState.oldPath.length - 1] as Step;
            }
            setNavState({
                oldPath: historyArray,
                curSlide: {step: curStep, direction},
                leavingSlide: {step: prevStep, direction}
            });
        } else {
            prevStep = historyArray[historyArray.length - 2] as Step;

            if (history.location.search.includes(PARAM_JUMP_BACK)) {
                // is jumpBack action
                direction = Direction.LEFT;
            } else {
                // next action
                direction = Direction.RIGHT;
            }
            setNavState({
                oldPath: historyArray,
                curSlide: {step: curStep, direction},
                leavingSlide: {step: prevStep, direction}
            });
        }
    }), [dispatch, history, navState.oldPath]);

    return {
        navState,
        nextSlide,
        prevSlide,
        jumpTo,
        renderStep
    };
}
